import React, { useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Switch,
  Divider,
  Progress,
  Chip,
} from '@nextui-org/react';
import { FaDownload, FaFileExport, FaFileCsv, FaFilePdf } from 'react-icons/fa';
import Icon from '../base/Icon';
import { toast } from 'react-toastify';
import { apiKeyService } from '../../services/apiKeyService';
import { DateRange } from './DateRangeSelector';

interface ExportManagerProps {
  className?: string;
  dateRange?: DateRange;
}

type ExportFormat = 'csv' | 'pdf';
type ExportType =
  | 'overview'
  | 'timeline'
  | 'models'
  | 'performance'
  | 'errors'
  | 'comprehensive';

const ExportManager: React.FC<ExportManagerProps> = ({
  className = '',
  dateRange,
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('csv');
  const [selectedType, setSelectedType] = useState<ExportType>('overview');
  const [isExporting, setIsExporting] = useState(false);
  const [includeCharts, setIncludeCharts] = useState(false);

  const exportTypes = [
    {
      key: 'overview',
      name: 'Overview Summary',
      description: 'Basic usage statistics and key metrics',
    },
    {
      key: 'timeline',
      name: 'Timeline Analysis',
      description: 'Usage trends over time with performance metrics',
    },
    {
      key: 'models',
      name: 'Model Usage Report',
      description: 'AI model distribution and performance comparison',
    },
    {
      key: 'performance',
      name: 'Performance Metrics',
      description: 'Detailed endpoint performance and response times',
    },
    {
      key: 'errors',
      name: 'Error Analysis',
      description: 'Error distribution and recent error logs',
    },
    {
      key: 'comprehensive',
      name: 'Comprehensive Report',
      description: 'Complete analytics package with all data',
    },
  ];

  const formatTypes = [
    {
      key: 'csv',
      name: 'CSV',
      icon: FaFileCsv,
      description: 'Excel-compatible spreadsheet format',
    },
    {
      key: 'pdf',
      name: 'PDF',
      icon: FaFilePdf,
      description: 'Professional report with charts and tables',
    },
  ];

  // Convert data to CSV format
  const convertToCSV = (data: any[], headers: string[]): string => {
    const csvHeaders = headers.join(',');
    const csvRows = data.map((row) =>
      headers
        .map((header) => {
          const value = row[header.toLowerCase().replace(/\s+/g, '_')] || '';
          return typeof value === 'string' && value.includes(',')
            ? `"${value}"`
            : value;
        })
        .join(',')
    );
    return [csvHeaders, ...csvRows].join('\n');
  };

  // Generate filename with timestamp and date range
  const generateFilename = (type: ExportType, format: ExportFormat): string => {
    const timestamp = new Date().toISOString().slice(0, 10);
    const typeMap = {
      overview: 'usage-overview',
      timeline: 'usage-timeline',
      models: 'model-usage',
      performance: 'endpoint-performance',
      errors: 'error-analysis',
      comprehensive: 'comprehensive-report',
    };

    let dateRangeSuffix = '';
    if (dateRange?.isAllTime) {
      dateRangeSuffix = '-all-time';
    } else if (dateRange?.startDate && dateRange?.endDate) {
      const startMonth = new Date(dateRange.startDate)
        .toISOString()
        .slice(0, 7);
      const endMonth = new Date(dateRange.endDate).toISOString().slice(0, 7);
      dateRangeSuffix =
        startMonth === endMonth
          ? `-${startMonth}`
          : `-${startMonth}-to-${endMonth}`;
    }

    return `dual-dialogue-${typeMap[type]}${dateRangeSuffix}-${timestamp}.${format}`;
  };

  // Download file
  const downloadFile = (
    content: string,
    filename: string,
    mimeType: string
  ) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export overview data
  const exportOverview = async (format: ExportFormat) => {
    const stats = await apiKeyService.getUsageStats();

    if (format === 'csv') {
      const overviewData = [
        { metric: 'Total Requests', value: stats.total_requests },
        { metric: 'Monthly Requests', value: stats.monthly_requests },
        { metric: 'Input Tokens', value: stats.input_tokens },
        { metric: 'Output Tokens', value: stats.output_tokens },
        {
          metric: 'Total Tokens',
          value: stats.input_tokens + stats.output_tokens,
        },
      ];

      const endpointData = stats.top_endpoints.map((endpoint) => ({
        endpoint: endpoint.endpoint,
        requests: endpoint.count,
        percentage: ((endpoint.count / stats.total_requests) * 100).toFixed(2),
      }));

      const overviewCSV = convertToCSV(overviewData, ['Metric', 'Value']);
      const endpointCSV = convertToCSV(endpointData, [
        'Endpoint',
        'Requests',
        'Percentage',
      ]);

      const combinedCSV =
        'API Usage Overview\n' +
        overviewCSV +
        '\n\n' +
        'Top Endpoints\n' +
        endpointCSV;

      downloadFile(
        combinedCSV,
        generateFilename('overview', 'csv'),
        'text/csv'
      );
    }
  };

  // Export timeline data
  const exportTimeline = async (format: ExportFormat) => {
    const timeline = dateRange?.isAllTime
      ? await apiKeyService.getUsageTimeline(30, undefined, undefined, true) // All time with proper flag
      : dateRange?.startDate && dateRange?.endDate
        ? await apiKeyService.getUsageTimeline(
            30,
            dateRange.startDate,
            dateRange.endDate
          )
        : await apiKeyService.getUsageTimeline(30);

    if (format === 'csv') {
      const timelineCSV = convertToCSV(timeline.timeline, [
        'Date',
        'Requests',
        'Input Tokens',
        'Output Tokens',
        'Avg Response Time',
        'Error Rate',
      ]);
      downloadFile(
        timelineCSV,
        generateFilename('timeline', 'csv'),
        'text/csv'
      );
    }
  };

  // Export model usage data
  const exportModels = async (format: ExportFormat) => {
    const models = dateRange?.isAllTime
      ? await apiKeyService.getModelUsage(30, undefined, undefined, true) // All time with proper flag
      : dateRange?.startDate && dateRange?.endDate
        ? await apiKeyService.getModelUsage(
            30,
            dateRange.startDate,
            dateRange.endDate
          )
        : await apiKeyService.getModelUsage();

    if (format === 'csv') {
      const modelsCSV = convertToCSV(
        models.models.map((model) => ({
          model: model.model,
          requests: model.requests,
          avg_response_time: model.avg_response_time,
          total_tokens: model.total_tokens,
          error_rate: model.error_rate,
        })),
        ['Model', 'Requests', 'Avg Response Time', 'Total Tokens', 'Error Rate']
      );
      downloadFile(modelsCSV, generateFilename('models', 'csv'), 'text/csv');
    }
  };

  // Export performance data
  const exportPerformance = async (format: ExportFormat) => {
    const performance = dateRange?.isAllTime
      ? await apiKeyService.getPerformanceMetrics(
          30,
          undefined,
          undefined,
          true
        ) // All time with proper flag
      : dateRange?.startDate && dateRange?.endDate
        ? await apiKeyService.getPerformanceMetrics(
            30,
            dateRange.startDate,
            dateRange.endDate
          )
        : await apiKeyService.getPerformanceMetrics();

    if (format === 'csv') {
      const performanceCSV = convertToCSV(
        performance.performance.map((perf) => ({
          endpoint: perf.endpoint,
          total_requests: perf.total_requests,
          avg_response_time: perf.avg_response_time,
          p95_response_time: perf.p95_response_time,
          avg_input_tokens: perf.avg_input_tokens,
          avg_output_tokens: perf.avg_output_tokens,
          error_rate: perf.error_rate,
        })),
        [
          'Endpoint',
          'Total Requests',
          'Avg Response Time',
          'P95 Response Time',
          'Avg Input Tokens',
          'Avg Output Tokens',
          'Error Rate',
        ]
      );
      downloadFile(
        performanceCSV,
        generateFilename('performance', 'csv'),
        'text/csv'
      );
    }
  };

  // Export error data
  const exportErrors = async (format: ExportFormat) => {
    const errors = dateRange?.isAllTime
      ? await apiKeyService.getErrorAnalysis(30, undefined, undefined, true) // All time with proper flag
      : dateRange?.startDate && dateRange?.endDate
        ? await apiKeyService.getErrorAnalysis(
            30,
            dateRange.startDate,
            dateRange.endDate
          )
        : await apiKeyService.getErrorAnalysis();

    if (format === 'csv') {
      const errorDistCSV = convertToCSV(errors.errors, [
        'Status Code',
        'Count',
        'Percentage',
      ]);

      const recentErrorsCSV = convertToCSV(
        errors.recent_errors.map((error) => ({
          created_at: error.created_at,
          endpoint: error.endpoint,
          method: error.method,
          status_code: error.status_code,
          error_message: error.error_message,
          model_used: error.model_used,
        })),
        [
          'Created At',
          'Endpoint',
          'Method',
          'Status Code',
          'Error Message',
          'Model Used',
        ]
      );

      const combinedCSV =
        'Error Distribution\n' +
        errorDistCSV +
        '\n\n' +
        'Recent Errors\n' +
        recentErrorsCSV;

      downloadFile(combinedCSV, generateFilename('errors', 'csv'), 'text/csv');
    }
  };

  // Export comprehensive report
  const exportComprehensive = async (format: ExportFormat) => {
    toast.info('Generating comprehensive report...');

    // Collect all data with date range support
    const [stats, timeline, models, performance, errors] = await Promise.all([
      apiKeyService.getUsageStats(),
      dateRange?.isAllTime
        ? apiKeyService.getUsageTimeline(30, undefined, undefined, true)
        : dateRange?.startDate && dateRange?.endDate
          ? apiKeyService.getUsageTimeline(
              30,
              dateRange.startDate,
              dateRange.endDate
            )
          : apiKeyService.getUsageTimeline(30),
      dateRange?.isAllTime
        ? apiKeyService.getModelUsage(30, undefined, undefined, true)
        : dateRange?.startDate && dateRange?.endDate
          ? apiKeyService.getModelUsage(
              30,
              dateRange.startDate,
              dateRange.endDate
            )
          : apiKeyService.getModelUsage(),
      dateRange?.isAllTime
        ? apiKeyService.getPerformanceMetrics(30, undefined, undefined, true)
        : dateRange?.startDate && dateRange?.endDate
          ? apiKeyService.getPerformanceMetrics(
              30,
              dateRange.startDate,
              dateRange.endDate
            )
          : apiKeyService.getPerformanceMetrics(),
      dateRange?.isAllTime
        ? apiKeyService.getErrorAnalysis(30, undefined, undefined, true)
        : dateRange?.startDate && dateRange?.endDate
          ? apiKeyService.getErrorAnalysis(
              30,
              dateRange.startDate,
              dateRange.endDate
            )
          : apiKeyService.getErrorAnalysis(),
    ]);

    if (format === 'csv') {
      // Generate individual CSV sections
      const overviewData = [
        { metric: 'Total Requests', value: stats.total_requests },
        { metric: 'Monthly Requests', value: stats.monthly_requests },
        { metric: 'Input Tokens', value: stats.input_tokens },
        { metric: 'Output Tokens', value: stats.output_tokens },
      ];

      const sections = [
        'DUAL DIALOGUE API - COMPREHENSIVE USAGE REPORT',
        `Generated: ${new Date().toLocaleString()}`,
        '',
        'OVERVIEW STATISTICS',
        convertToCSV(overviewData, ['Metric', 'Value']),
        '',
        'TIMELINE DATA (30 DAYS)',
        convertToCSV(timeline.timeline, [
          'Date',
          'Requests',
          'Input Tokens',
          'Output Tokens',
          'Avg Response Time',
          'Error Rate',
        ]),
        '',
        'MODEL USAGE',
        convertToCSV(models.models, [
          'Model',
          'Requests',
          'Avg Response Time',
          'Total Tokens',
          'Error Rate',
        ]),
        '',
        'ENDPOINT PERFORMANCE',
        convertToCSV(performance.performance, [
          'Endpoint',
          'Total Requests',
          'Avg Response Time',
          'P95 Response Time',
          'Error Rate',
        ]),
        '',
        'ERROR ANALYSIS',
        convertToCSV(errors.errors, ['Status Code', 'Count', 'Percentage']),
      ];

      const comprehensiveCSV = sections.join('\n');
      downloadFile(
        comprehensiveCSV,
        generateFilename('comprehensive', 'csv'),
        'text/csv'
      );
    }
  };

  // Main export handler
  const handleExport = async () => {
    try {
      setIsExporting(true);

      switch (selectedType) {
        case 'overview':
          await exportOverview(selectedFormat);
          break;
        case 'timeline':
          await exportTimeline(selectedFormat);
          break;
        case 'models':
          await exportModels(selectedFormat);
          break;
        case 'performance':
          await exportPerformance(selectedFormat);
          break;
        case 'errors':
          await exportErrors(selectedFormat);
          break;
        case 'comprehensive':
          await exportComprehensive(selectedFormat);
          break;
      }

      toast.success('Export completed successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Icon icon={FaFileExport} className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Export Reports</h3>
        </div>
      </CardHeader>
      <CardBody className="space-y-6">
        {/* Export Type Selection */}
        <div>
          <h4 className="font-medium mb-3">Report Type</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {exportTypes.map((type) => (
              <div
                key={type.key}
                className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedType === type.key
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedType(type.key as ExportType)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h5 className="font-medium text-sm">{type.name}</h5>
                    <p className="text-xs text-gray-500 mt-1">
                      {type.description}
                    </p>
                  </div>
                  {selectedType === type.key && (
                    <Chip color="primary" size="sm" variant="flat">
                      Selected
                    </Chip>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Divider />

        {/* Format Selection */}
        <div>
          <h4 className="font-medium mb-3">Export Format</h4>
          <div className="flex gap-3">
            {formatTypes.map((format) => (
              <div
                key={format.key}
                className={`flex-1 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedFormat === format.key
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedFormat(format.key as ExportFormat)}
              >
                <div className="flex items-center gap-3">
                  <Icon icon={format.icon} className="w-5 h-5 text-primary" />
                  <div>
                    <h5 className="font-medium text-sm">{format.name}</h5>
                    <p className="text-xs text-gray-500">
                      {format.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Divider />

        {/* Export Options */}
        <div>
          <h4 className="font-medium mb-3">Options</h4>
          <div className="space-y-3">
            {selectedFormat === 'pdf' && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Include Charts</p>
                  <p className="text-xs text-gray-500">
                    Add visual charts to the PDF report
                  </p>
                </div>
                <Switch
                  size="sm"
                  isSelected={includeCharts}
                  onValueChange={setIncludeCharts}
                />
              </div>
            )}
          </div>
        </div>

        <Divider />

        {/* Export Summary */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h5 className="font-medium text-sm mb-2">Export Summary</h5>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Report Type:</span>
              <span className="font-medium">
                {exportTypes.find((t) => t.key === selectedType)?.name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Format:</span>
              <span className="font-medium uppercase">{selectedFormat}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Filename:</span>
              <span className="font-mono text-xs">
                {generateFilename(selectedType, selectedFormat)}
              </span>
            </div>
          </div>
        </div>

        {/* Export Button */}
        <Button
          color="primary"
          size="lg"
          className="w-full"
          startContent={<Icon icon={FaDownload} className="w-4 h-4" />}
          onPress={handleExport}
          isLoading={isExporting}
        >
          {isExporting ? 'Generating Export...' : 'Export Report'}
        </Button>

        {isExporting && (
          <div className="space-y-2">
            <Progress
              size="sm"
              isIndeterminate
              color="primary"
              className="max-w-full"
            />
            <p className="text-xs text-center text-gray-500">
              Collecting and formatting data...
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default ExportManager;
